import { AxiosInstance } from 'axios';
import { createApiClient } from '../http/client';
import {
  ClinicSyncJobResponse,
  CreateSmsMessagePayload,
  ExportMessagesPayload,
  FindAllAppointmentsParams,
  FindAllMembersParams,
  FindAllMessagesParams,
  GetMessageHistoryParams,
  MemberClinicReconcileResult,
  MemberCommunicationStatus,
  MemberWithCommunicationStatus,
  MessageLog,
  MessageLogsByDateAndLastOutboundResponse,
  MessageLogsBySystemPhoneAndDate,
  SimplifiedAppointment,
  SimplifiedMessageLog,
  TrackingNumberResponse,
  TriggerClinicSyncPayload,
  UpdateAppointmentConfirmationStatusPayload,
  UpdateMemberClinicPayload,
  UpdateMemberOptedOutPayload,
  UpdateMemberUnreachablePayload,
  UpdateMessageResolutionStatusPayload,
} from '../../interfaces/appointments';
import { ApiResponse } from '../../interfaces/common';
import { AuthService } from '../auth';

const APPOINTMENTS_API_BASE_URL = process.env.REACT_APP_PUBLIC_APPOINTMENTS_API_BASE_URL;

class MessagesService {
  private axios: AxiosInstance;

  constructor() {
    this.axios = createApiClient({
      baseURL: APPOINTMENTS_API_BASE_URL,
    });
  }

  public async findAllMessages(
    params: FindAllMessagesParams = {},
  ): Promise<ApiResponse<SimplifiedMessageLog[]>> {
    try {
      const response = (
        await this.axios.get<ApiResponse<SimplifiedMessageLog[]>>(`/communications/messages`, {
          params,
          headers: {
            Authorization: `Bearer ${AuthService.getAuth()?.auth_token}`,
          },
        })
      ).data;

      if (response.statusCode) {
        return {
          success: false,
          statusCode: response.statusCode,
          message: response.message,
        };
      }

      return {
        success: true,
        statusCode: response.statusCode,
        message: response.message,
        data: response.data,
        pagination: response.pagination,
      };
    } catch (error) {
      console.error(error);

      return {
        success: false,
        statusCode: 500,
        message: 'An error has occurred, please contact support',
      };
    }
  }

  public async findAllMessagesGroupedByDate(params: {
    coreMemberId: number;
    orderBy?: 'asc' | 'desc';
  }): Promise<ApiResponse<MessageLogsByDateAndLastOutboundResponse>> {
    try {
      const response = (
        await this.axios.get<ApiResponse<MessageLogsByDateAndLastOutboundResponse>>(
          `/communications/messages/grouped-by-date`,
          {
            params,
            headers: {
              Authorization: `Bearer ${AuthService.getAuth()?.auth_token}`,
            },
          },
        )
      ).data;

      if (response.statusCode) {
        return {
          success: false,
          statusCode: response.statusCode,
          message: response.message,
        };
      }

      return {
        success: true,
        statusCode: response.statusCode,
        message: response.message,
        data: response.data as MessageLogsByDateAndLastOutboundResponse,
        pagination: response.pagination,
      };
    } catch (error) {
      console.error(error);

      return {
        success: false,
        statusCode: 500,
        message: 'An error has occurred, please contact support',
      };
    }
  }

  public async getMessageHistory(
    params: GetMessageHistoryParams,
  ): Promise<ApiResponse<MessageLogsBySystemPhoneAndDate>> {
    try {
      const response = (
        await this.axios.get<ApiResponse<MessageLogsBySystemPhoneAndDate>>(
          `/communications/messages/history`,
          {
            params,
            headers: {
              Authorization: `Bearer ${AuthService.getAuth()?.auth_token}`,
            },
          },
        )
      ).data;

      if (response.statusCode) {
        return {
          success: false,
          statusCode: response.statusCode,
          message: response.message,
        };
      }

      return {
        success: true,
        statusCode: response.statusCode,
        message: response.message,
        data: response.data,
        pagination: response.pagination,
      };
    } catch (error) {
      console.error(error);

      return {
        success: false,
        statusCode: 500,
        message: 'An error has occurred, please contact support',
      };
    }
  }

  public async sendSmsMessage(data: CreateSmsMessagePayload): Promise<ApiResponse<MessageLog>> {
    try {
      const response = (
        await this.axios.post<ApiResponse<MessageLog>>(`/communications/messages`, data, {
          headers: {
            Authorization: `Bearer ${AuthService.getAuth()?.auth_token}`,
          },
        })
      ).data;

      if (response.statusCode) {
        return {
          success: false,
          statusCode: response.statusCode,
          message: response.message,
        };
      }

      return {
        success: true,
        statusCode: response.statusCode,
        message: response.message,
        data: response as unknown as MessageLog,
      };
    } catch (error) {
      console.error(error);

      return {
        success: false,
        statusCode: 500,
        message: 'An error has occurred, please contact support',
      };
    }
  }

  public async findAllAppointments(
    params: FindAllAppointmentsParams,
  ): Promise<ApiResponse<SimplifiedAppointment[]>> {
    try {
      const response = (
        await this.axios.get<ApiResponse<SimplifiedAppointment[]>>(`/appointments`, {
          params,
          headers: {
            Authorization: `Bearer ${AuthService.getAuth()?.auth_token}`,
          },
        })
      ).data;

      if (response.statusCode) {
        return {
          success: false,
          statusCode: response.statusCode,
          message: response.message,
        };
      }

      return {
        success: true,
        statusCode: response.statusCode,
        message: response.message,
        data: response.data,
        pagination: response.pagination,
      };
    } catch (error) {
      console.error(error);

      return {
        success: false,
        statusCode: 500,
        message: 'An error has occurred, please contact support',
      };
    }
  }

  public async findAllMembers(
    params: FindAllMembersParams,
  ): Promise<ApiResponse<MemberWithCommunicationStatus[]>> {
    try {
      const response = (
        await this.axios.get<ApiResponse<MemberWithCommunicationStatus[]>>(
          '/communications/members',
          {
            params,
            headers: {
              Authorization: `Bearer ${AuthService.getAuth()?.auth_token}`,
            },
          },
        )
      ).data;

      if (response.statusCode) {
        return {
          success: false,
          statusCode: response.statusCode,
          message: response.message,
        };
      }

      return {
        success: true,
        statusCode: response.statusCode,
        message: response.message,
        data: response.data,
        pagination: response.pagination,
      };
    } catch (error) {
      console.error(error);

      return {
        success: false,
        statusCode: 500,
        message: 'An error has occurred, please contact support',
      };
    }
  }

  public async updateAppointmentConfirmationStatus(
    payload: UpdateAppointmentConfirmationStatusPayload,
  ): Promise<ApiResponse<SimplifiedAppointment>> {
    try {
      const response = (
        await this.axios.put<ApiResponse<SimplifiedAppointment>>(`/appointments`, payload, {
          headers: {
            Authorization: `Bearer ${AuthService.getAuth()?.auth_token}`,
          },
        })
      ).data;

      if (response.statusCode) {
        return {
          success: false,
          statusCode: response.statusCode,
          message: response.message,
        };
      }

      return {
        success: true,
        statusCode: response.statusCode,
        message: response.message,
        data: response as unknown as SimplifiedAppointment,
      };
    } catch (error) {
      console.error(error);

      return {
        success: false,
        statusCode: 500,
        message: 'An error has occurred, please contact support',
      };
    }
  }

  public async updateMemberOptedOutMethods(
    payload: UpdateMemberOptedOutPayload,
  ): Promise<ApiResponse<MemberCommunicationStatus>> {
    try {
      const response = (
        await this.axios.put<ApiResponse<MemberCommunicationStatus>>(
          `/communications/members/${payload.coreMemberId}/preferences`,
          {
            smsOptedOutStatus: payload.smsOptedOutStatus,
            emailOptedOutStatus: payload.emailOptedOutStatus,
          },
          {
            headers: {
              Authorization: `Bearer ${AuthService.getAuth()?.auth_token}`,
            },
          },
        )
      ).data;

      if (response.statusCode) {
        return {
          success: false,
          statusCode: response.statusCode,
          message: response.message,
        };
      }

      return {
        success: true,
        statusCode: response.statusCode,
        message: response.message,
        data: response as unknown as MemberCommunicationStatus,
      };
    } catch (error) {
      console.error(error);

      return {
        success: false,
        statusCode: 500,
        message: 'An error has occurred, please contact support',
      };
    }
  }

  public async updateMemberClinic(
    payload: UpdateMemberClinicPayload,
  ): Promise<ApiResponse<MemberClinicReconcileResult>> {
    try {
      const response = (
        await this.axios.put<ApiResponse<MemberClinicReconcileResult>>(
          `/communications/members/${payload.coreMemberId}/clinic`,
          {
            mode: payload.mode,
            clinicId: payload.clinicId,
            clinicName: payload.clinicName,
          },
          {
            headers: {
              Authorization: `Bearer ${AuthService.getAuth()?.auth_token}`,
            },
          },
        )
      ).data;

      if (response.statusCode) {
        return {
          success: false,
          statusCode: response.statusCode,
          message: response.message,
        };
      }

      return {
        success: true,
        statusCode: response.statusCode,
        message: response.message,
        data: response as unknown as MemberClinicReconcileResult,
      };
    } catch (error) {
      console.error(error);

      return {
        success: false,
        statusCode: 500,
        message: 'An error has occurred, please contact support',
      };
    }
  }

  public async triggerClinicSync(
    payload: TriggerClinicSyncPayload,
  ): Promise<ApiResponse<ClinicSyncJobResponse>> {
    try {
      const response = (
        await this.axios.post<ApiResponse<ClinicSyncJobResponse>>(
          `/communications/clinic-sync`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${AuthService.getAuth()?.auth_token}`,
            },
          },
        )
      ).data;

      if (response.statusCode) {
        return {
          success: false,
          statusCode: response.statusCode,
          message: response.message,
        };
      }

      return {
        success: true,
        statusCode: response.statusCode,
        message: response.message,
        data: response as unknown as ClinicSyncJobResponse,
      };
    } catch (error) {
      console.error(error);

      return {
        success: false,
        statusCode: 500,
        message: 'An error has occurred, please contact support',
      };
    }
  }

  public async updateMessageResolutionStatus(
    messageId: number,
    payload: UpdateMessageResolutionStatusPayload,
  ): Promise<ApiResponse<MessageLog>> {
    try {
      const response = (
        await this.axios.put<ApiResponse<MessageLog>>(
          `/communications/messages/${messageId}/resolution`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${AuthService.getAuth()?.auth_token}`,
            },
          },
        )
      ).data;

      if (response.statusCode) {
        return {
          success: false,
          statusCode: response.statusCode,
          message: response.message,
        };
      }

      return {
        success: true,
        statusCode: response.statusCode,
        message: response.message,
        data: response as unknown as MessageLog,
      };
    } catch (error) {
      console.error(error);

      return {
        success: false,
        statusCode: 500,
        message: 'An error has occurred, please contact support',
      };
    }
  }

  public async getTrackingNumbers(): Promise<ApiResponse<TrackingNumberResponse>> {
    const authToken = AuthService.getAuth()?.auth_token;
    try {
      const response = (
        await this.axios.get<ApiResponse<TrackingNumberResponse>>(
          `/communications/messages/numbers`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
            params: {
              coreProviderId: (await AuthService.getProfile(authToken)).user.id,
            },
          },
        )
      ).data;

      if (response.statusCode) {
        return {
          success: false,
          statusCode: response.statusCode,
          message: response.message,
        };
      }

      return {
        success: true,
        statusCode: response.statusCode,
        message: response.message,
        data: response as unknown as TrackingNumberResponse,
      };
    } catch (error) {
      console.error(error);

      return {
        success: false,
        statusCode: 500,
        message: 'An error has occurred, please contact support',
      };
    }
  }

  public async updateMemberUnreachableStatus(
    payload: UpdateMemberUnreachablePayload,
  ): Promise<ApiResponse<MemberCommunicationStatus>> {
    try {
      const response = (
        await this.axios.put<ApiResponse<MemberCommunicationStatus>>(
          `/communications/members/${payload.coreMemberId}/unreachable`,
          {
            isUnreachable: payload.isUnreachable,
            unreachableReason: payload.unreachableReason,
          },
          {
            headers: {
              Authorization: `Bearer ${AuthService.getAuth()?.auth_token}`,
            },
          },
        )
      ).data;

      if (response.statusCode) {
        return {
          success: false,
          statusCode: response.statusCode,
          message: response.message,
        };
      }

      return {
        success: true,
        statusCode: response.statusCode,
        message: response.message,
        data: response as unknown as MemberCommunicationStatus,
      };
    } catch (error) {
      console.error(error);

      return {
        success: false,
        statusCode: 500,
        message: 'An error has occurred, please contact support',
      };
    }
  }

  public async exportMessages(payload: ExportMessagesPayload): Promise<ApiResponse<void>> {
    try {
      const response = (
        await this.axios.post<ApiResponse<void>>(`/communications/messages/export`, payload, {
          headers: {
            Authorization: `Bearer ${AuthService.getAuth()?.auth_token}`,
          },
        })
      ).data;

      if (response.statusCode) {
        return {
          success: false,
          statusCode: response.statusCode,
          message: response.message,
        };
      }

      return {
        success: true,
        statusCode: response.statusCode,
        message: response.message,
      };
    } catch (error) {
      console.error(error);

      return {
        success: false,
        statusCode: 500,
        message: 'An error has occurred, please contact support',
      };
    }
  }
}

const messagesService = new MessagesService();

export default messagesService;
